/**
 * TODO EL COMPORTAMIENTO DE LA WEB.
 *
 * Sin librerías salvo Lenis, que es la del scroll con inercia. Lo que en la
 * versión de Next hacían React y una librería de animación aquí lo hace el
 * navegador solo: un observador para las entradas y unas cuantas líneas para
 * el pase de foco. Pesa una fracción y se comporta igual.
 *
 * Cada bloque se vuelve a arrancar cuando el editor de temas recarga una
 * sección, para que lo que toques en el panel se vea al momento sin recargar.
 */

(function () {
  'use strict';

  const sinMovimiento = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Enseña algo que estaba oculto y le deja arrancar su transición.
   *
   * Entre quitar el `hidden` y añadir la clase hace falta que el navegador
   * calcule el estado inicial, o la transición no ocurre: pasa de golpe. Leer
   * `offsetWidth` fuerza ese cálculo en el acto.
   *
   * Antes esto se hacía pidiendo un fotograma de animación, pero en una
   * pestaña en segundo plano el navegador no da fotogramas, y el panel se
   * quedaba abierto pero invisible. Así no depende de eso.
   */
  function mostrarConTransicion(nodo, clase) {
    nodo.hidden = false;
    void nodo.offsetWidth;
    nodo.classList.add(clase);
  }

  /* ---------------------------------------------------------------- */
  /* SCROLL CON INERCIA                                                */
  /* ---------------------------------------------------------------- */

  function scrollSuave() {
    if (!window.Lenis || sinMovimiento()) return;
    if (window.__lenis) return;

    const lenis = new window.Lenis({ autoRaf: true });
    window.__lenis = lenis;
  }

  /* ---------------------------------------------------------------- */
  /* EL PASE DE FOCO DEL NOMBRE DE PORTADA                             */
  /* ---------------------------------------------------------------- */

  /**
   * No enfoca de golpe: el círculo persigue al ratón con retraso y, mientras
   * viaja, la capa nítida va suave. Solo al alcanzar el cursor entra a foco.
   * Es el mismo gesto que hace un foquista, y por eso se nota la intención.
   *
   * La posición se escribe como variable de CSS directamente sobre el nodo:
   * así el efecto va a la velocidad del ratón sin rehacer nada más.
   *
   * Hay UN solo oyente para toda la vida de la página, y es él quien busca el
   * nombre cada vez que te mueves. No al revés. En Shopify las secciones se
   * sustituyen enteras cada vez que tocas un ajuste en el editor: un oyente
   * atado al nodo de entonces se queda hablándole a un elemento que ya no
   * está en la página, y el efecto se muere sin decir nada.
   */
  const foco = {
    contenedor: null,
    capa: null,
    objetivo: { x: 0, y: 0 },
    actual: { x: 0, y: 0 },
    corriendo: false,
    raf: 0,
  };

  /** Si hay ratón o no se decide en cada gesto, no una vez al cargar: un
   *  navegador puede tardar en saber qué tipo de puntero tiene. */
  const sinRaton = window.matchMedia('(hover: none)');

  /**
   * El nombre que hay ahora mismo en la página. Si es otro que el de la última
   * vez —porque el editor ha recargado la sección— se reinicia el estado y el
   * foco vuelve a arrancar desde el centro.
   */
  function nombreEnPantalla() {
    const contenedor = document.querySelector('[data-foco]');
    if (!contenedor) return null;

    const capa = contenedor.querySelector('.foco-nitido');
    if (!capa) return null;

    if (contenedor !== foco.contenedor) {
      foco.contenedor = contenedor;
      foco.capa = capa;
      // El foco arranca en el centro del bloque, no fuera de pantalla: así el
      // primer pase es corto y natural.
      const caja = contenedor.getBoundingClientRect();
      foco.objetivo.x = caja.width / 2;
      foco.objetivo.y = caja.height / 2;
      foco.actual.x = foco.objetivo.x;
      foco.actual.y = foco.objetivo.y;
      foco.corriendo = false;
    }

    return foco;
  }

  function bucleDelFoco() {
    const capa = foco.capa;
    if (!capa || !capa.isConnected) {
      foco.corriendo = false;
      return;
    }

    const ajuste = foco.contenedor.dataset;
    /** Cuánto persigue el foco al cursor en cada fotograma. Más bajo = más lento. */
    const seguimiento = parseFloat(ajuste.seguimiento) || 0.15;
    /** Desenfoque máximo mientras el foco viaja, en píxeles. */
    const desenfoqueMax = parseFloat(ajuste.desenfoqueMax) || 3;
    /** Cuánto desenfoque por píxel de distancia: lo pronto que entra a foco. */
    const porPixel = parseFloat(ajuste.desenfoquePorPixel) || 0.022;

    foco.actual.x += (foco.objetivo.x - foco.actual.x) * seguimiento;
    foco.actual.y += (foco.objetivo.y - foco.actual.y) * seguimiento;

    const distancia = Math.hypot(
      foco.objetivo.x - foco.actual.x,
      foco.objetivo.y - foco.actual.y,
    );
    const desenfoque = Math.min(distancia * porPixel, desenfoqueMax);

    capa.style.setProperty('--mx', foco.actual.x.toFixed(1) + 'px');
    capa.style.setProperty('--my', foco.actual.y.toFixed(1) + 'px');
    capa.style.setProperty('--nitidez', desenfoque.toFixed(2) + 'px');

    // Distancia por debajo de la cual damos el foco por hecho y paramos.
    if (distancia < 0.3) {
      capa.style.setProperty('--nitidez', '0px');
      foco.corriendo = false;
      return;
    }

    foco.raf = requestAnimationFrame(bucleDelFoco);
  }

  function arrancarFoco() {
    if (foco.corriendo) return;
    foco.corriendo = true;
    foco.raf = requestAnimationFrame(bucleDelFoco);
  }

  window.addEventListener(
    'pointermove',
    (e) => {
      if (sinRaton.matches || sinMovimiento()) return;

      const f = nombreEnPantalla();
      if (!f) return;

      const r = f.contenedor.getBoundingClientRect();
      f.objetivo.x = e.clientX - r.left;
      f.objetivo.y = e.clientY - r.top;
      arrancarFoco();
    },
    { passive: true },
  );

  /**
   * Al cambiar de pestaña el navegador congela las animaciones, y el pase se
   * quedaría parado a medio camino. Al volver, lo damos por terminado en vez
   * de dejar el nombre borroso hasta que muevas el ratón.
   */
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    cancelAnimationFrame(foco.raf);
    foco.corriendo = false;

    const capa = foco.capa;
    if (!capa || !capa.isConnected) return;

    foco.actual.x = foco.objetivo.x;
    foco.actual.y = foco.objetivo.y;
    capa.style.setProperty('--mx', foco.actual.x.toFixed(1) + 'px');
    capa.style.setProperty('--my', foco.actual.y.toFixed(1) + 'px');
    capa.style.setProperty('--nitidez', '0px');
  });

  /* ---------------------------------------------------------------- */
  /* ENTRADAS AL BAJAR POR LA PÁGINA                                   */
  /* ---------------------------------------------------------------- */

  function entradas(raiz) {
    const piezas = (raiz || document).querySelectorAll('.entra:not(.dentro)');
    if (!piezas.length) return;

    if (sinMovimiento() || !('IntersectionObserver' in window)) {
      piezas.forEach((p) => p.classList.add('dentro'));
      return;
    }

    const observador = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((entrada) => {
          if (!entrada.isIntersecting) return;
          // Un escalón por columna, como en la web de Next: la segunda ficha
          // de cada fila entra un pelín después que la primera.
          const retraso = parseFloat(entrada.target.dataset.retraso) || 0;
          entrada.target.style.transitionDelay = retraso + 's';
          entrada.target.classList.add('dentro');
          observador.unobserve(entrada.target);
        });
      },
      { rootMargin: '-80px' },
    );

    piezas.forEach((p) => observador.observe(p));
  }

  /* ---------------------------------------------------------------- */
  /* REPRODUCTOR A PANTALLA COMPLETA                                   */
  /* ---------------------------------------------------------------- */

  /**
   * La dirección del reproductor de cada sitio.
   *
   * youtube-nocookie no deja cookies de seguimiento hasta que se le da al
   * play, y `rel=0` evita que al terminar salgan vídeos de otros canales.
   */
  function direccionDeVideo(proveedor, id) {
    const p = new URLSearchParams({
      autoplay: '1',
      mute: '0',
      controls: '1',
      rel: '0',
      modestbranding: '1',
      playsinline: '1',
    });

    if (proveedor === 'youtube') {
      return 'https://www.youtube-nocookie.com/embed/' + id + '?' + p;
    }
    if (proveedor === 'vimeo') {
      return 'https://player.vimeo.com/video/' + id + '?autoplay=1&dnt=1';
    }
    if (proveedor === 'instagram') {
      // El embed oficial. No admite autoplay: el visitante pulsa play dentro.
      return 'https://www.instagram.com/reel/' + id + '/embed/';
    }
    return null;
  }

  let cajaVideo = null;

  function montarReproductor() {
    if (cajaVideo) return cajaVideo;

    cajaVideo = document.createElement('div');
    cajaVideo.className = 'reproductor';
    cajaVideo.hidden = true;
    cajaVideo.innerHTML =
      '<button class="micro reproductor__cerrar" type="button" aria-label="Cerrar vídeo">cerrar (esc)</button>' +
      '<span class="micro reproductor__titulo"></span>' +
      '<div class="reproductor__marco"></div>';

    document.body.appendChild(cajaVideo);

    cajaVideo.addEventListener('click', (e) => {
      if (e.target.closest('.reproductor__marco')) return;
      cerrarVideo();
    });

    return cajaVideo;
  }

  function abrirVideo(proveedor, id, titulo) {
    const caja = montarReproductor();
    const marco = caja.querySelector('.reproductor__marco');
    const url = direccionDeVideo(proveedor, id);

    // Los reels de Instagram son 9:16: el marco cambia a vertical.
    marco.classList.toggle('reproductor__marco--vertical', proveedor === 'instagram');

    if (url) {
      const marca = document.createElement('iframe');
      marca.src = url;
      marca.title = titulo || 'Vídeo';
      marca.allow =
        'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
      marca.allowFullscreen = true;
      marco.replaceChildren(marca);
    } else {
      // Archivo propio servido desde la tienda.
      const video = document.createElement('video');
      video.src = id;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      marco.replaceChildren(video);
    }

    caja.querySelector('.reproductor__titulo').textContent = titulo || '';

    // Congela el scroll del fondo mientras el vídeo está abierto.
    document.body.style.overflow = 'hidden';
    if (window.__lenis) window.__lenis.stop();

    mostrarConTransicion(caja, 'reproductor--visible');

    // Llevamos el foco al botón de cerrar. Si el foco se queda dentro del
    // iframe de YouTube, la tecla Esc no llega a esta página: por eso el botón
    // siempre está visible y el fondo también cierra al clicar.
    caja.querySelector('.reproductor__cerrar').focus();
  }

  function cerrarVideo() {
    if (!cajaVideo || cajaVideo.hidden) return;
    cajaVideo.classList.remove('reproductor--visible');
    cajaVideo.querySelector('.reproductor__marco').replaceChildren();
    cajaVideo.hidden = true;
    document.body.style.overflow = '';
    if (window.__lenis) window.__lenis.start();
  }

  /* ---------------------------------------------------------------- */
  /* SELECTOR DE REELS DE LA PORTADA                                   */
  /* ---------------------------------------------------------------- */

  function abrirReels() {
    const selector = document.querySelector('[data-reels]');
    if (!selector) return;
    document.body.style.overflow = 'hidden';
    if (window.__lenis) window.__lenis.stop();
    mostrarConTransicion(selector, 'reels--visible');
  }

  function cerrarReels() {
    const selector = document.querySelector('[data-reels]');
    if (!selector || selector.hidden) return;
    selector.classList.remove('reels--visible');
    selector.hidden = true;
    document.body.style.overflow = '';
    if (window.__lenis) window.__lenis.start();
  }

  /* ---------------------------------------------------------------- */
  /* CONTADOR DE SEGUIDORES                                            */
  /* ---------------------------------------------------------------- */

  /**
   * Separa los millares con punto, siempre.
   *
   * En español no se pone punto en números de cuatro cifras, pero en una
   * columna junto a 12.000 y 10.000 un «3000» desentona, así que se fuerza.
   */
  function conMillares(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function contadores(raiz) {
    const cuentas = (raiz || document).querySelectorAll('[data-contador]');
    if (!cuentas.length) return;

    cuentas.forEach((nodo) => {
      const hasta = parseInt(nodo.dataset.contador, 10);
      if (!hasta) return;

      if (sinMovimiento() || !('IntersectionObserver' in window)) {
        nodo.textContent = '+' + conMillares(hasta);
        return;
      }

      const observador = new IntersectionObserver(
        (entradas) => {
          entradas.forEach((entrada) => {
            if (!entrada.isIntersecting) return;
            observador.unobserve(nodo);

            // El número de verdad ya viene escrito desde el servidor. Solo lo
            // ponemos a cero justo antes de contar, y no al cargar la página:
            // así, si algo impide que la cuenta llegue a arrancar, se queda el
            // número bueno en vez de un «+0» eterno.
            nodo.textContent = '+0';

            const duracion = 1600;
            const arranque = performance.now();

            function paso(ahora) {
              const t = Math.min((ahora - arranque) / duracion, 1);
              // Frena al final, como todo lo demás del sitio.
              const suave = 1 - Math.pow(1 - t, 3);
              nodo.textContent = '+' + conMillares(Math.round(hasta * suave));
              if (t < 1) requestAnimationFrame(paso);
            }

            requestAnimationFrame(paso);
          });
        },
        { rootMargin: '-40px' },
      );

      observador.observe(nodo);
    });
  }

  /* ---------------------------------------------------------------- */
  /* EMAIL QUE SE COPIA AL CLICAR                                      */
  /* ---------------------------------------------------------------- */

  function emailCopiable(raiz) {
    const botones = (raiz || document).querySelectorAll('[data-copiar]');

    botones.forEach((boton) => {
      if (boton.dataset.listo) return;
      boton.dataset.listo = '1';

      boton.addEventListener('click', () => {
        const email = boton.dataset.copiar;
        navigator.clipboard.writeText(email).then(() => {
          const aviso = boton.querySelector('[data-aviso]');
          if (!aviso) return;
          aviso.classList.add('visible');
          setTimeout(() => aviso.classList.remove('visible'), 1800);
        });
      });
    });
  }

  /* ---------------------------------------------------------------- */
  /* ARRANQUE                                                          */
  /* ---------------------------------------------------------------- */

  // Un solo oyente para todo lo que se clica, en lugar de uno por ficha.
  document.addEventListener('click', (e) => {
    const disparador = e.target.closest('[data-video-id]');
    if (disparador) {
      e.preventDefault();
      abrirVideo(
        disparador.dataset.videoProveedor || 'youtube',
        disparador.dataset.videoId,
        disparador.dataset.videoTitulo,
      );
      return;
    }

    if (e.target.closest('[data-abre-reels]')) {
      e.preventDefault();
      abrirReels();
      return;
    }

    if (e.target.closest('[data-cierra-reels]')) {
      e.preventDefault();
      cerrarReels();
      return;
    }

    if (e.target.closest('.reproductor__cerrar')) {
      e.preventDefault();
      cerrarVideo();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    cerrarVideo();
    cerrarReels();
  });

  /**
   * La miniatura de máxima resolución no existe para todos los vídeos: los
   * subidos en baja no la tienen. Cuando falla, se pide la mediana.
   */
  document.addEventListener(
    'error',
    (e) => {
      const img = e.target;
      if (img.tagName !== 'IMG') return;
      if (!img.src.includes('maxresdefault')) return;
      img.src = img.src.replace('maxresdefault', 'hqdefault');
    },
    true,
  );

  function arrancarTodo(raiz) {
    // El pase de foco no se arranca aquí: tiene su propio oyente permanente,
    // que busca el nombre cada vez que mueves el ratón.
    entradas(raiz);
    contadores(raiz);
    emailCopiable(raiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scrollSuave();
      arrancarTodo(document);
    });
  } else {
    scrollSuave();
    arrancarTodo(document);
  }

  // El editor de temas cambia una sección sin recargar la página: hay que
  // volver a arrancar lo que viva dentro de ella.
  document.addEventListener('shopify:section:load', (e) => arrancarTodo(e.target));
  document.addEventListener('shopify:section:unload', cerrarReels);
})();
